import { useCallback, useEffect, useState } from 'react';

import { personsApi } from './personnelApi';

/**
 * A person's photograph, as a URL an `<img>` can point at.
 *
 * **It has to be fetched rather than linked, and that is not a detail.** The endpoint carries
 * `personnel.read` and the SPA authenticates with a bearer token in a header, which an
 * `<img src>` cannot send — and the API is on its own origin, so a bare `/api/...` path would
 * not even reach it. So the bytes come back through the same authenticated client as
 * everything else and become an object URL local to this tab.
 *
 * The consequence worth knowing: the URL this returns is meaningless anywhere else. It cannot
 * be stored, shared, or put in a DTO — which is the right shape for a face out of `rsys_pii`.
 *
 * **No photograph is not a failure.** A record without one is the ordinary case, so a 404
 * resolves to null and the avatar falls back to its initials.
 */
export function usePersonPhoto(personId: string | null): {
  url: string | null;
  loading: boolean;
  /** Re-fetches, for after a write. */
  reload: () => void;
} {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(personId !== null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    Revoked when it is REPLACED, not when the fetch that replaces it starts. React runs this
    cleanup after the render carrying the new URL has committed, so the image is never
    pointing at a URL that has already been revoked — the same pairing `<PhotoField>` uses for
    its own preview.
  */
  useEffect(
    () => () => {
      if (url) URL.revokeObjectURL(url);
    },
    [url],
  );

  useEffect(() => {
    if (!personId) {
      setUrl(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    setLoading(true);

    personsApi
      .fetchPhoto(personId, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) return;
        setUrl(blob ? URL.createObjectURL(blob) : null);
      })
      .catch(() => {
        // A photograph that will not load is an avatar with initials, never a broken page.
        if (!controller.signal.aborted) setUrl(null);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [personId, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { url, loading, reload };
}
